/** @jsxImportSource @emotion/react */
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as authSelectors from "../../../auth/authSelectors";
import { useSelector } from "react-redux";
import { AdyenStoreListItem } from "../../models/adyen-business-line";
import { Table } from "../../../../components/table-components/Table";
import { colors } from "../../../../components/colors";
import { TableRow } from "../../../../components/table-components/TableRow";
import { TableBody } from "../../../../components/table-components/TableBody";
import { TableRowHeader } from "../../../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../../../components/table-components/TableCellHeaderText";
import { TableCellLink } from "../../../../components/table-components/TableCellLink";
import { StyledText } from "../../../../assets/typography/typography";
import { TableEllipsisDropdown } from "../../../../components/TableEllipsisDropdown";
import { DropdownItemLink } from "../../../common/DropdownItemLink";
import { routes } from "../../../../appRoutingHelpers";
import { useParams } from "react-router";
import { usePushAdyenStoreToAdyen } from "../../service/adyen-account-holders-api";
import { DropdownItemButton } from "../../../common/DropdownItemButton";
import { getErrorMessage } from "../../../../error-handler";
import { toastr } from "react-redux-toastr";
import { ActionSpinner } from "../../../../components/ActionSpinner";

type Props = {
  stores: AdyenStoreListItem[];
};

export const AdyenAccountHolderStoresTable = ({ stores }: Props) => {
  const { id, storeId } = useParams();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const {
    mutateAsync: pushAdyenStoreToAdyen,
    isLoading: isPushingAdyenStoreToAdyen,
  } = usePushAdyenStoreToAdyen(storeId);

  return (
    <Table>
      <thead>
        <TableRowHeader>
          <TableCellHeaderText>Description</TableCellHeaderText>
          <TableCellHeaderText>Country</TableCellHeaderText>
          <TableCellHeaderText>Reference</TableCellHeaderText>
          <TableCellHeaderText>Account holder reference</TableCellHeaderText>
          <th />
        </TableRowHeader>
      </thead>
      <TableBody>
        {stores.map((store, i) => {
          const href = routes.adyenAccountHolderStoreEdit(
            organizationKeyName,
            id,
            store.id
          );

          return (
            <TableRow key={i}>
              <TableCellLink href={href} className="pl-5">
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {store.description || "-"}
                </StyledText>
              </TableCellLink>
              <TableCellLink href={href}>
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {store.countryCode || "-"}
                </StyledText>
              </TableCellLink>
              <TableCellLink href={href}>
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {store.reference || "-"}
                </StyledText>
              </TableCellLink>
              <TableCellLink href={href}>
                <StyledText
                  className="bodyTextRegular mb-0"
                  color={colors.textPrimary}
                >
                  {store.accountHolderReference || "-"}
                </StyledText>
              </TableCellLink>
              <TableEllipsisDropdown
                menu={{
                  items: [
                    {
                      key: "1",
                      label: (
                        <DropdownItemLink
                          icon={<FontAwesomeIcon icon={solid("pen")} />}
                          to={href}
                        >
                          Edit
                        </DropdownItemLink>
                      ),
                    },
                    {
                      key: "2",
                      label: (
                        <DropdownItemButton
                          onClick={async () => {
                            try {
                              await pushAdyenStoreToAdyen({
                                data: { id: store.id },
                              });
                              toastr.success(
                                "Success",
                                "Store pushed to Adyen"
                              );
                            } catch (error) {
                              const errorMessage = getErrorMessage(error);
                              toastr.error("Error", errorMessage);
                            }
                          }}
                          type="button"
                          disabled={isPushingAdyenStoreToAdyen}
                          icon={<FontAwesomeIcon icon={solid("upload")} />}
                        >
                          {isPushingAdyenStoreToAdyen ? (
                            <ActionSpinner />
                          ) : (
                            "Push to Adyen"
                          )}
                        </DropdownItemButton>
                      ),
                    },
                  ],
                }}
              />
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
